<template>
  <div
    class="artwork-multi-dimensions"
    :class="{ 'artwork-multi-dimensions--multi-upload': isMultiUpload }"
  >
    <PresetSelector
      v-if="isArtworkUploaded && showMultiSize"
      :size-presets="sizePresets"
      :selected-presets="selectedPresets"
      :disabled="!isArtworkUploaded"
      :artwork-url="artworkUrl"
      :is-multi-upload="isMultiUpload"
      :index="index"
      @select="handlePresetSelect"
    />
    <div
      v-if="showFreeTransfersBadge"
      class="artwork-multi-dimensions__free-transfers-badge-wrapper"
    >
      <FreeTransfersBadge
        :message="freeTransfersBadgeMessage"
        class="artwork-multi-dimensions__free-transfers-badge"
        data-testid="artwork-multi-dimensions-free-transfers-badge"
      />
    </div>
    <template v-for="presetSize in Object.values(sizesGroupedByPreset)" :key="presetSize.preset">
      <MultiSizeGroup
        v-if="presetSize.preset === 'custom'"
        :count="presetSize.count"
        :price="presetSize.price"
        title="Custom Size"
        :show-title="isArtworkUploaded"
        :show-delete-button="selectedPresets.length > 1"
        @delete="handlePresetSelect"
      >
        <div
          v-for="(size, sizeIndex) in presetSize.items"
          :key="sizeIndex"
          class="artwork-multi-dimensions__form-number-row"
          :class="{
            'artwork-multi-dimensions__form-number-row--first': sizeIndex === 0
          }"
        >
          <div class="artwork-multi-dimensions__form-number-row-content">
            <div
              class="artwork-multi-dimensions__control"
              :class="{
                'artwork-multi-dimensions__control--gang-lock': gangPadlock && !!width
              }"
            >
              <label v-if="sizeIndex === 0" class="artwork-multi-dimensions__label" :for="widthId">
                Width (Inch)
              </label>
              <input
                :id="widthId"
                :value="size.width"
                class="artwork-multi-dimensions__field artwork-multi-dimensions__field--number js-dtf-width-input"
                :class="{
                  'artwork-multi-dimensions__field--invalid': isFieldInValid(size.id)
                }"
                max="100"
                min="0.1"
                :name="widthId"
                step=".01"
                type="number"
                data-hj-allow
                :data-size-index="size.index"
                data-field="width"
                :disabled="disableFields"
                :readonly="readonly"
                @click="handleInputClick"
                @input="e => handleValueInput(e.target)"
                @blur="onBlur"
              />
            </div>
            <UseSvg icon="cross" class="artwork-multi-dimensions__multiplier" />
            <div
              class="artwork-multi-dimensions__control"
              :class="{
                'artwork-multi-dimensions__control--gang-lock': lockHeight
              }"
            >
              <label v-if="sizeIndex === 0" class="artwork-multi-dimensions__label" :for="heightId">
                {{ heightLabel }}
              </label>
              <input
                :id="heightId"
                :value="size.height"
                class="artwork-multi-dimensions__field artwork-multi-dimensions__field--number js-dtf-height-input"
                :class="{
                  'artwork-multi-dimensions__field--invalid': isFieldInValid(size.id)
                }"
                max="100"
                min="0.1"
                :name="heightId"
                step=".01"
                type="number"
                data-hj-allow
                :data-size-index="size.index"
                data-field="height"
                :disabled="disableFields"
                :readonly="readonlyHeight"
                @click="handleInputClick"
                @input="e => handleValueInput(e.target)"
                @blur="onBlur"
              />
            </div>
            <div
              class="artwork-multi-dimensions__control artwork-multi-dimensions__control--quantity"
            >
              <label v-if="sizeIndex === 0" class="artwork-multi-dimensions__label" for="quantity">
                Quantity
              </label>
              <QuantityInput
                :quantity="size.quantity"
                :index="size.index"
                :disabled="disableFields"
                :is-multi-upload="isMultiUpload"
                :variant-id="variantId"
                @on-change="handleQuantityUpdate"
              />
              <div
                v-if="showPricing"
                class="artwork-multi-dimensions__price"
                :class="{
                  'artwork-multi-dimensions__price--loading':
                    isPricingLoading || size.quantity === 0
                }"
              >
                {{ n(totalPrice(size.id), "currency") }}
              </div>
            </div>
            <button
              v-if="sizesAndQuantities.length > 1"
              type="button"
              :data-size-index="size.index"
              class="artwork-multi-dimensions__remove-button"
              @click="removeSize"
            >
              <UseSvg class="artwork-multi-dimensions__remove-button-icon" icon="trash" />
            </button>
          </div>
          <div v-if="errors[size.id]" class="artwork-multi-dimensions__error-message">
            {{ errors[size.id].message }}
          </div>
        </div>
        <template #footer>
          <button
            v-if="isArtworkUploaded"
            type="button"
            class="artwork-multi-dimensions__add-button"
            :class="{
              'artwork-multi-dimensions__add-button--standard-artwork': isStandardArtwork
            }"
            @click="addSize"
          >
            Add another size
          </button>
        </template>
      </MultiSizeGroup>
      <MultiSizeGroup
        v-else
        is-preset
        :count="presetSize.count"
        :price="presetSize.price"
        :title="presetSize.preset"
        :show-delete-button="selectedPresets.length > 1"
        @delete="handlePresetSelect"
        @open-size-guide="toggleSizeGuide"
      >
        <CustomSizeSelector
          :preset="presetSize.preset"
          :sizes="presetSize.items"
          :artwork-id="String(artwork.id)"
          :errors="errorGroupedByPreset[presetSize.preset]"
          @on-change="handleQuantityUpdate"
        />
      </MultiSizeGroup>
    </template>
    <div v-if="!printAsIs && !disabled" class="artwork-multi-dimensions__free-note">
      Padding for easy cutting is added for free
    </div>
  </div>
  <SizeGuide v-if="sizeGuidePreset" :preset="sizeGuidePreset" @on-close="toggleSizeGuide" />
</template>

<script setup>
import { submitDtfEvent } from "common/utils/mixpanel/submit-dtf-events";
import debounce from "lodash.debounce";
import isEqual from "lodash.isequal";
import UseSvg from "components/jiffy/vue-cart/UseSvg.vue";
import {
  trackRudderstackImageEdited,
  trackRudderstackImagePlacementSelection,
  trackRudderstackImagePlacementQuantity
} from "common/utils/rudderstack/tracker";
import { n } from "components/jiffy/vue-cart/helpers/currency";
import { computed, ref, watch, onMounted } from "vue";
import useArtworkValidation from "../common/composables/useArtworkValidation";
import QuantityInput from "./QuantityInput.vue";
import CustomSizeSelector from "./CustomSizeSelector.vue";
import PresetSelector from "./PresetSelector.vue";
import MultiSizeGroup from "./MultiSizeGroup.vue";
import SizeGuide from "./SizeGuide.vue";
import FreeTransfersBadge from "../common/FreeTransfersBadge.vue";
import {
  MIN_DPI,
  PRINT_AS_IS_MIN_DPI,
  PRINT_AS_IS_MIN_WIDTH,
  MAX_SIZE_FIELD_VALUE,
  DEFAULT_VALUES,
  GANG_DEFAULT_VALUES,
  MAX_QUANTITY_VALUE,
  MAX_PRINT_SIZES_GROUPED
} from "../common/constants";
import { getRandomId, getTitleCaseString, scaleToFitRatio } from "../utils";
import {
  getImageMetadataInfo,
  getRangeString,
  groupByPreset,
  isWidthAndHeightValid,
  getProportionalEdge
} from "./utils";
import { useDtfStore } from "../store";

// Props
const props = defineProps({
  artwork: {
    type: Object,
    default: null
  },
  index: {
    type: Number,
    default: 0
  },
  disabled: {
    type: Boolean,
    default: false
  },
  readonly: {
    type: Boolean,
    default: false
  },
  isMultiUpload: {
    type: Boolean,
    default: false
  },
  showFreeTransfersBadge: {
    type: Boolean,
    default: false
  }
});

// Store setup
const store = useDtfStore();

const artworkUrl = computed(() => props.artwork?.url);

// State
const previousData = ref([
  {
    width: store.printAsIs ? GANG_DEFAULT_VALUES.width : DEFAULT_VALUES.width,
    height: store.printAsIs ? GANG_DEFAULT_VALUES.height : DEFAULT_VALUES.height
  }
]);

const errors = ref({});

const sizeGuidePreset = ref(null);

const sizeChanged = ref(false);

const backgroundChanged = ref({
  changed: false,
  isBgRemoved: false
});

// Computed
const isPricingLoading = computed(() => store.isPricingLoading);

const printAsIs = computed(() => store.printAsIs);

const showMultiSize = computed(() => store.showMultiSize);

const isStandardArtwork = computed(() => store.isStandardArtwork);

const showMultiSizeBeforeUpload = computed(() => store.showMultiSizeBeforeUpload);

const sticker = computed(() => store.sticker);

const proColor = computed(() => store.isProColor);

const isMultiUpload = computed(() => props.isMultiUpload);

// Computed
const variantId = computed(() => store.variantId);

const gangPadlock = computed(() => printAsIs.value && !store.showModal);

const lockHeight = computed(() => gangPadlock.value && !!props.metadata);

const editType = computed(() => {
  if (sizeChanged.value) {
    return "Size Adjustment";
  }
  if (props.artwork.enhanceImage) {
    return "Jiffy Print Perfect";
  }

  return "Print as uploaded";
});

const isArtworkLoading = computed(
  () =>
    props.artwork?.isLoading || (isStandardArtwork.value && !props.artwork?.enhancementCompleted)
);

const isArtworkUploaded = computed(() => {
  const artworkId = parseInt(props.artwork?.id, 10);
  if (Number.isNaN(artworkId)) {
    return false;
  }

  if (showMultiSizeBeforeUpload.value) {
    return typeof artworkId === "number";
  }

  if (isStandardArtwork.value) {
    return !props.artwork?.isLoading && props.artwork?.enhancementCompleted;
  }

  return typeof artworkId === "number" && !props.artwork?.isLoading;
});

const showPricing = computed(() => props.index === 0 || isArtworkUploaded.value);

const disableFields = computed(() => {
  if (showMultiSizeBeforeUpload.value) {
    return props.disabled && !isArtworkUploaded.value;
  }
  return props.disabled;
});

const defaultCustomSize = computed(() => {
  const values = props.index === 0 ? store.simulation : store.initialValues;

  return [
    {
      width: values.width,
      height: values.height,
      quantity: values.quantity,
      preset: "custom",
      id: getRandomId()
    }
  ];
});

const sizesAndQuantities = computed(() => {
  if (props.artwork.id && props.artwork.sizesAndQuantities.length > 0) {
    return props.artwork.sizesAndQuantities;
  }
  return defaultCustomSize.value;
});

const sizesGroupedByPreset = computed(() => {
  const grouped = groupByPreset(sizesAndQuantities.value);

  // Apply BOGO-adjusted prices to group totals
  const artworkId = String(props.artwork.id);
  Object.values(grouped).forEach(group => {
    let bogoAdjustedPrice = 0;
    let hasBogoPrice = false;

    group.items.forEach(item => {
      const sizeKey = `${item.width}x${item.height}`;
      const bogoPrice = store.getBogoAdjustedPrice(artworkId, item.id, sizeKey);

      if (bogoPrice) {
        bogoAdjustedPrice += bogoPrice.discountedTotalPrice;
        hasBogoPrice = true;
      } else if (item.unitPrice) {
        bogoAdjustedPrice += Number(item.unitPrice) * item.quantity;
      }
    });

    if (hasBogoPrice) {
      group.price = bogoAdjustedPrice;
    }
  });

  if (grouped.custom) {
    const { custom, ...others } = grouped;
    return { ...others, custom };
  }

  return grouped;
});

const selectedPresets = computed(() => Object.keys(sizesGroupedByPreset.value));

const errorGroupedByPreset = computed(() =>
  Object.values(errors.value).reduce(
    (acc, error) => ({ ...acc, [error.preset]: [...(acc[error.preset] || []), error] }),
    {}
  )
);

const readonlyHeight = computed(() => {
  if (store.showModal && store.printAsIs) return props.readonly;
  if (!lockHeight.value) return false;
  return props.readonly;
});

const widthId = computed(() => `artwork[${props.index}][variants][${store.variantId}][width]`);

const heightId = computed(() => `artwork[${props.index}][variants][${store.variantId}][height]`);

const metadata = computed(() => {
  const metadataSource = props.artwork;

  const { metadata: artworkMetadata, withBgMetadata } = metadataSource || {};
  const { removeBackground } = props.artwork || {};

  if (!withBgMetadata || Object.keys(withBgMetadata).length === 0) {
    return artworkMetadata;
  }

  return removeBackground ? artworkMetadata : withBgMetadata;
});

const minDpi = computed(() => {
  if (store.printAsIs) return PRINT_AS_IS_MIN_DPI;
  return metadata.value?.upscalingEnabled ? Number(metadata.value.qualityUpscaleDpi) : MIN_DPI;
});

const heightLabel = computed(() => (store.printAsIs ? "Length (Feet)" : "Height (Inch)"));

const minWidthSetting = computed(() =>
  store.printAsIs ? PRINT_AS_IS_MIN_WIDTH : store.dtfMinWidth
);

const imageMetadataInfo = computed(() =>
  getImageMetadataInfo({
    metadata: metadata.value,
    sizesAndQuantities: sizesAndQuantities.value,
    minDpi: minDpi.value,
    artwork: props.artwork
  })
);

const sizePresets = computed(() => {
  if (!props.artwork.width || !props.artwork.height) {
    return showMultiSizeBeforeUpload.value
      ? Object.keys(MAX_PRINT_SIZES_GROUPED).map(key => ({
          id: key,
          isValid: true,
          title: getTitleCaseString(key)
        }))
      : [];
  }

  return Object.entries(MAX_PRINT_SIZES_GROUPED)
    .map(([key, dimensions]) => ({
      id: key,
      isValid: dimensions.every(({ width, height }) => {
        const scaledSize = scaleToFitRatio(width, height, metadata.value?.aspectRatio);
        return scaledSize.width >= 1 && scaledSize.height >= 1;
      }),
      title: getTitleCaseString(key)
    }))
    .filter(item => item.isValid);
});

const allowedPresets = computed(() => sizePresets.value.map(preset => preset.id));

const blankItemsCount = computed(() => store.blankItemsCount);

const transfersInCartCount = computed(() => store.transfersInCartCount);

const selectedQuantityTotal = computed(() =>
  sizesAndQuantities.value.reduce((total, size) => total + (parseInt(size.quantity, 10) || 0), 0)
);

const totalTransfersCount = computed(() => {
  const currentQuantity = isMultiUpload.value ? store.totalQuantity : selectedQuantityTotal.value;
  const cartTransfers = transfersInCartCount.value || 0;
  return currentQuantity + cartTransfers;
});

const remainingFreeTransfers = computed(() =>
  Math.max(0, blankItemsCount.value - totalTransfersCount.value)
);

const freeTransfersBadgeMessage = computed(
  () => `${remainingFreeTransfers.value} of ${blankItemsCount.value} FREE Transfers Left`
);

// Methods
const loadPricingAndDates = debounce(() => {
  store.getArtworksPricing();
  store.updatePdpDates();
}, 2500);

const getNewPricingAndDates = () => {
  store.setIsPricingLoading(true);
  loadPricingAndDates();
};

const updateSizesAndQuantities = async (newSizesAndQuantities, isSizesEdited = false) => {
  store.updateArtworkValues({
    index: props.artwork.originalIndex,
    isSizesEdited: showMultiSizeBeforeUpload.value && isSizesEdited,
    ...{ sizesAndQuantities: newSizesAndQuantities }
  });
  store.updatePreviewDpi({ artworkId: props.artwork.id });
  await store.downscaleOriginalImage({ artworkId: props.artwork.id });
  getNewPricingAndDates();
};

const calculateCustomDimensions = size => ({
  width: size.width,
  height: getProportionalEdge({
    value: size.width,
    field: "height",
    aspectRatio: metadata.value?.aspectRatio,
    currentDimensions: sizesAndQuantities.value[sizesAndQuantities.value.length - 1]
  })
});

const calculatePresetDimensions = ({ size, preset }) => {
  const presetSize = MAX_PRINT_SIZES_GROUPED[preset].find(item => size === getRangeString(item));
  if (!presetSize) return null;
  return scaleToFitRatio(presetSize.width, presetSize.height, metadata.value?.aspectRatio);
};

const resetErrors = () => {
  errors.value = {};
};

const sendValidationEvent = (eventType, errorType, data) => {
  let validationMetadata = {
    ...data,
    has_image: !props.artwork.isFile && !props.artwork.isSimulated,
    pdp_type: store.pdpType,
    business_line: store.businessLine,
    page: "pdp"
  };

  if (!props.artwork.isFile) {
    validationMetadata = { ...validationMetadata, ...imageMetadataInfo.value };
  }

  submitDtfEvent(eventType, {
    type: errorType,
    ...validationMetadata
  });
};

const buildValidationConfig = () => ({
  minWidth: minWidthSetting.value,
  minHeight: store.dtfMinHeight,
  maxWidth: store.dtfMaxWidth,
  maxHeight: store.dtfMaxHeight,
  isPrintAsIs: printAsIs.value,
  metadata: metadata.value,
  originalDimensions: { originalWidth: props.artwork.width, originalHeight: props.artwork.height },
  onValidation: sendValidationEvent
});

const sendValidationWarningEvent = () => {
  const warning = props.artwork.metadata?.warnings?.keys[0];
  // only for gang sheet uploads
  if (!printAsIs.value || !warning) return;

  const validationMetadata = {
    ...imageMetadataInfo.value,
    has_image: !props.artwork.isFile && !props.artwork.isSimulated,
    pdp_type: store.pdpType,
    business_line: store.businessLine,
    page: "pdp"
  };

  submitDtfEvent("image_validation_warning", {
    type: warning,
    ...validationMetadata
  });
};

const setValidationErrorMessageToArtwork = () => {
  const errorList = Object.values(errors.value);
  const firstError = errorList[0];

  store.setValidationErrorMessage({
    index: props.artwork.originalIndex,
    isMultiSizeError: errorList.length > 0,
    message: firstError?.message || null,
    type: firstError?.type || null
  });
};

const validator = useArtworkValidation(buildValidationConfig());

const validateValues = () => {
  if (printAsIs.value && !props.artwork.isSimulated) return;

  resetErrors();

  const activeSizes = sizesAndQuantities.value.filter(({ quantity }) => Number(quantity) > 0);

  const validationErrors = Object.fromEntries(
    activeSizes
      .map(item => {
        const error = validator.validateDimensions(item);
        return error ? [item.id, error] : null;
      })
      .filter(Boolean)
  );

  errors.value = validationErrors;
  setValidationErrorMessageToArtwork();
  sendValidationWarningEvent();
};

const recalculateSizes = async () => {
  const newSizes = sizesAndQuantities.value
    .filter(size => allowedPresets.value.includes(size.preset))
    .map(size => {
      const newDimensions =
        size.preset === "custom"
          ? calculateCustomDimensions(size)
          : calculatePresetDimensions(size);

      return {
        ...size,
        ...newDimensions
      };
    });

  await updateSizesAndQuantities(newSizes);
  validateValues();
};

const toggleSizeGuide = (preset = null) => {
  sizeGuidePreset.value = preset;
};

const getCorrectDimensionsFromMetadata = () => {
  if (!isStandardArtwork.value || store.catalogNumber !== "DTF001" || !props.artwork?.id) {
    return { width: props.artwork?.width, height: props.artwork?.height };
  }

  const { enhanceImage, enhancementTier, removeBackground, enhancedMetadata, originalMetadata } =
    props.artwork || {};

  let width;
  let height;

  // For Print As Is (either product-level or tab selection), always use original metadata
  if (store.printAsIs || enhancementTier === "print_original") {
    width = originalMetadata?.widthInch || metadata.value?.widthInch;
    height = originalMetadata?.heightInch || metadata.value?.heightInch;
  } else if (enhanceImage && enhancementTier !== "custom") {
    // Enhanced (not custom): use enhanced or withBg metadata dimensions
    if (removeBackground) {
      width = enhancedMetadata?.widthInch || metadata.value?.widthInch;
      height = enhancedMetadata?.heightInch || metadata.value?.heightInch;
    } else {
      width = props.artwork?.withBgMetadata?.widthInch || metadata.value?.widthInch;
      height = props.artwork?.withBgMetadata?.heightInch || metadata.value?.heightInch;
    }
  } else {
    // Not enhanced or custom tier: use original metadata
    width = originalMetadata?.widthInch || metadata.value?.widthInch;
    height = originalMetadata?.heightInch || metadata.value?.heightInch;
  }

  return { width: width || props.artwork?.width, height: height || props.artwork?.height };
};

const updateDimensionsFromMetadata = async () => {
  if (!isStandardArtwork.value || store.catalogNumber !== "DTF001" || !props.artwork?.id) {
    return;
  }

  const { width: customWidth, height: customHeight } = getCorrectDimensionsFromMetadata();

  if (!customWidth || !customHeight) return;

  // Recalculate sizes based on their preset type
  const updatedSizes = sizesAndQuantities.value.map(sizeItem => {
    if (sizeItem.preset === "custom") {
      // Custom sizes: use metadata dimensions directly
      return {
        ...sizeItem,
        width: customWidth,
        height: customHeight
      };
    }

    // Preset sizes: recalculate based on preset type with new aspect ratio
    const newDimensions = calculatePresetDimensions({
      size: sizeItem.size,
      preset: sizeItem.preset
    });
    return {
      ...sizeItem,
      ...newDimensions
    };
  });

  await updateSizesAndQuantities(updatedSizes, false);
};

const handlePresetSelect = ({ preset, sizes }) => {
  const isPresetPresent = preset in sizesGroupedByPreset.value;
  const rudderstackTrackProperties = {
    design_size_selection: preset,
    artwork_id: props.artwork.id,
    design_dimensions: MAX_PRINT_SIZES_GROUPED[preset]
  };

  if (isPresetPresent) {
    const filteredSizes = sizesAndQuantities.value.filter(size => size.preset !== preset);
    updateSizesAndQuantities(filteredSizes, true);
    trackRudderstackImagePlacementSelection("Removed", rudderstackTrackProperties);
  } else {
    const newSizes =
      preset === "custom"
        ? [
            {
              preset: "custom",
              width: getCorrectDimensionsFromMetadata().width,
              quantity: 1,
              height: getCorrectDimensionsFromMetadata().height,
              id: getRandomId()
            }
          ]
        : sizes.map(size => ({
            ...scaleToFitRatio(size.width, size.height, metadata.value?.aspectRatio),
            quantity: size.quantity ?? 0,
            size: getRangeString(size),
            id: getRandomId(),
            preset
          }));

    updateSizesAndQuantities([...sizesAndQuantities.value, ...newSizes], true);
    trackRudderstackImagePlacementSelection("Selected", rudderstackTrackProperties);
  }
};

const updateArtworksPricingAndTrackSizeChange = (oldPrice, newPrice) => {
  if (isArtworkLoading.value) return;

  const eventPayload = {
    // eslint-disable-next-line no-nested-ternary
    product_line: sticker.value ? "sticker" : proColor.value ? "pro color" : "dtf",
    business_line: store.businessLine,
    artwork: props.artwork,
    edit_type: editType.value,
    artworks_count: store.artworks.length,
    location: "pdp",
    old_price: oldPrice,
    new_price: newPrice
  };

  if (backgroundChanged.value.changed) {
    eventPayload.remove_background_toggle_switch = backgroundChanged.value.isBgRemoved
      ? "switched_on"
      : "switched_off";
    backgroundChanged.value = { changed: false, isBgRemoved: false };
  }

  sizeChanged.value = false;

  trackRudderstackImageEdited(eventPayload);
};

const totalPrice = sizeId => {
  const size = sizesAndQuantities.value.find(({ id }) => id === sizeId);
  const pricing = size?.unitPrice ? size : store.simulation;

  // Check for BOGO-adjusted price
  const artworkId = String(props.artwork.id);
  const sizeKey = `${size.width}x${size.height}`;
  const bogoPrice = store.getBogoAdjustedPrice(artworkId, sizeId, sizeKey);

  if (bogoPrice) {
    return bogoPrice.discountedTotalPrice.toFixed(2);
  }

  return (pricing.unitPrice * size.quantity).toFixed(2);
};

const isFieldInValid = id =>
  errors.value[id] && errors.value[id].message && !errors.value[id].isFieldValid;

const addSize = () => {
  const dimensions = getCorrectDimensionsFromMetadata();
  const newValue = {
    preset: "custom",
    id: getRandomId(),
    quantity: 1,
    width: dimensions.width,
    height: dimensions.height
  };
  updateSizesAndQuantities([...sizesAndQuantities.value, newValue], true);
  submitDtfEvent("multi_size_artwork", {
    pdp_type: store.pdpType,
    business_line: store.businessLine,
    page: "pdp",
    action: "add_size",
    size_count: sizesAndQuantities.value.length
  });
  trackRudderstackImagePlacementSelection("Size Added", {
    design_size_selection: "custom",
    artwork_id: props.artwork.id,
    design_dimensions: [{ width: newValue.width, height: newValue.height }],
    quantity: 1
  });
};

const removeSize = e => {
  const { sizeIndex } = e.target.dataset;
  const targetSize = sizesAndQuantities.value[Number(sizeIndex)];
  updateSizesAndQuantities(
    sizesAndQuantities.value.filter((_, index) => index !== Number(sizeIndex)),
    true
  );
  submitDtfEvent("multi_size_artwork", {
    pdp_type: store.pdpType,
    business_line: store.businessLine,
    page: "pdp",
    action: "remove_size",
    size_count: sizesAndQuantities.value.length
  });
  trackRudderstackImagePlacementSelection("Size Removed", {
    design_size_selection: "custom",
    artwork_id: props.artwork.id,
    design_dimensions: [{ width: targetSize.width, height: targetSize.height }],
    quantity: targetSize.quantity
  });
};

const trackSizeChanges = (newData, oldData, quantity) => {
  if (
    isArtworkLoading.value ||
    (newData?.width === oldData?.width && newData?.height === oldData?.height)
  )
    return;

  let sizeChange;

  const area = newData.width * newData.height;
  const oldArea = oldData.width * oldData.height;
  if (area === 0 && oldArea === 0) {
    sizeChange = "none";
  } else if (area > oldArea) {
    sizeChange = "larger";
  } else {
    sizeChange = "smaller";
  }
  const sizePercentChange =
    oldArea === 0 ? 0 : Math.round((Math.abs(area - oldArea) / oldArea) * 100);

  submitDtfEvent("size_modified", {
    width: newData.width,
    height: newData.height,
    previous_width: oldData.width,
    previous_height: oldData.height,
    size_change: sizeChange,
    business_line: store.businessLine,
    size_percent_change: sizePercentChange,
    has_image: !store.simulation,
    page: "pdp",
    pdp_type: store.pdpType
  });

  trackRudderstackImagePlacementSelection("Size Edited", {
    design_size_selection: "custom",
    artwork_id: props.artwork.id,
    design_dimensions: [{ width: newData.width, height: newData.height }],
    quantity
  });
};

const onBlur = e => {
  const { sizeIndex } = e.target.dataset;

  const dimension = sizesAndQuantities.value[sizeIndex];

  // compare object instead of individual width & height
  const currentData = {
    width: dimension.width,
    height: dimension.height
  };

  if (isEqual(currentData, previousData.value[sizeIndex])) {
    return;
  }

  validateValues();

  if (isWidthAndHeightValid(dimension)) {
    trackSizeChanges(currentData, previousData.value[sizeIndex], dimension.quantity);
    previousData.value[sizeIndex] = currentData;
    getNewPricingAndDates();
    sizeChanged.value = true;
  }
};

const buildRudderstackTrackProperties = (index, sizeData, quantity) => {
  const { preset } = sizesAndQuantities.value[index];
  const designDimensions = MAX_PRINT_SIZES_GROUPED[preset];

  return {
    design_size_selection: preset,
    artwork_id: props.artwork.id,
    design_dimensions: designDimensions,
    blanks_size: sizeData.size?.trim(),
    quantity
  };
};

const updateQuantity = (value, index) => {
  updateSizesAndQuantities(
    sizesAndQuantities.value.map((size, idx) => {
      if (index === idx) {
        const quantity = value.length > 0 ? Math.max(1, Number(value)) : value;
        const previousQuantity = Number(size.quantity) || 0;
        const trackProperties = buildRudderstackTrackProperties(index, size, quantity);
        trackRudderstackImagePlacementQuantity(trackProperties);
        submitDtfEvent("dtf_artwork_quantity_change", {
          artwork_id: props.artwork.id,
          size_index: index,
          preset: size.preset,
          previous_quantity: previousQuantity,
          quantity,
          business_line: store.businessLine,
          pdp_type: store.pdpType,
          is_multi_upload: isMultiUpload.value,
          design_dimensions: trackProperties.design_dimensions,
          blanks_size: trackProperties.blanks_size
        });
        return { ...size, quantity };
      }
      return size;
    }),
    true
  );
  if (index === 0) {
    store.setSimulation({ quantity: value });
  }
  getNewPricingAndDates();
};

/* eslint-disable max-lines-per-function */
const autoFillInputs = (field, inputValue, sizeIndex) => {
  if (!props.artwork || !sizesAndQuantities.value[sizeIndex]) return;

  const value = Number(inputValue) > MAX_SIZE_FIELD_VALUE ? MAX_SIZE_FIELD_VALUE : inputValue;

  let newDimensions = {
    width: sizesAndQuantities.value[sizeIndex].width,
    height: sizesAndQuantities.value[sizeIndex].height
  };

  previousData.value[sizeIndex] = { ...newDimensions };

  if (field === "width") {
    newDimensions = {
      width: value,
      height: getProportionalEdge({
        value,
        field: "height",
        aspectRatio: metadata.value?.aspectRatio,
        currentDimensions: sizesAndQuantities.value[sizeIndex]
      })
    };
    store.setSimulation({ widthEdited: true });
  }

  if (field === "height") {
    newDimensions = {
      width: getProportionalEdge({
        value,
        field: "width",
        aspectRatio: metadata.value?.aspectRatio,
        currentDimensions: sizesAndQuantities.value[sizeIndex]
      }),
      height: value
    };
  }

  const newSizesAndQuantities = sizesAndQuantities.value.map((item, index) =>
    index === sizeIndex ? { ...item, ...newDimensions } : item
  );

  if (sizeIndex === 0) {
    store.setSimulation({ ...newDimensions });
  }

  const editedSizeId = sizesAndQuantities.value[sizeIndex].id;

  updateSizesAndQuantities(newSizesAndQuantities, true);

  store.updateArtworkValues({
    index: props.artwork.originalIndex,
    lastEditedSizeId: editedSizeId
  });
};

const handleValueInput = target => {
  const {
    value,
    dataset: { sizeIndex, field }
  } = target;

  if (field === "quantity") {
    updateQuantity(value, Number(sizeIndex));
  } else {
    resetErrors();
    autoFillInputs(field, value, Number(sizeIndex));
    if (Number(value) > MAX_SIZE_FIELD_VALUE) {
      validateValues();
    }
  }
};

const handleInputClick = e => {
  // Done so that the input field blur works properly on firefox when using the input arrows.
  e.target.focus();
};

const handleQuantityUpdate = ({ value, index }) => {
  const val = Number(value) > MAX_QUANTITY_VALUE ? MAX_QUANTITY_VALUE : value;

  const valueObject = {
    value: val,
    dataset: { sizeIndex: index, field: "quantity" }
  };

  handleValueInput(valueObject);
};

// Watch
watch(
  () => props.artwork,
  (newArtwork, oldArtwork) => {
    if (newArtwork.isFile || newArtwork.isSimulated) return;
    if (!!newArtwork.metadata?.errors && !newArtwork.isValid) return;
    if (oldArtwork.removeBackground !== newArtwork.removeBackground) {
      backgroundChanged.value = {
        changed: true,
        isBgRemoved: newArtwork.removeBackground
      };
    }
  }
);

watch(
  () => props.artwork,
  debounce((newArtwork, oldArtwork) => {
    if (newArtwork.isFile || newArtwork.isSimulated) return;
    if (!!newArtwork.metadata?.errors && !newArtwork.isValid) return;

    const oldArtworkSizePrices = oldArtwork.sizesAndQuantities.map(({ unitPrice }) => unitPrice);
    const newArtworkSizePrices = newArtwork.sizesAndQuantities.map(({ unitPrice }) => unitPrice);
    const bgChanged = backgroundChanged.value.changed;

    if (
      !isPricingLoading.value &&
      oldArtworkSizePrices.length === newArtworkSizePrices.length &&
      (!isEqual(oldArtworkSizePrices, newArtworkSizePrices) || bgChanged)
    ) {
      oldArtworkSizePrices.forEach((oldPrice, index) => {
        if ((oldPrice && oldPrice !== newArtworkSizePrices[index]) || bgChanged) {
          updateArtworksPricingAndTrackSizeChange(oldPrice, newArtworkSizePrices[index]);
        }
      });
    }

    const newSizeAdded =
      newArtwork.sizesAndQuantities.length !== oldArtwork.sizesAndQuantities.length;
    const enhancementSwitched = newArtwork.enhancementTier !== oldArtwork.enhancementTier;

    if (
      (oldArtwork.isLoading && !newArtwork.isLoading) ||
      bgChanged ||
      newSizeAdded ||
      enhancementSwitched
    ) {
      validateValues();
    }
  }, 300) // Wait for ending of getting new prices
);

watch(
  () => metadata.value,
  (newMetadata, oldMetadata) => {
    if (newMetadata && oldMetadata && newMetadata.aspectRatio !== oldMetadata.aspectRatio) {
      recalculateSizes();
    }
  }
);

watch(
  () => [
    props.artwork?.enhanceImage,
    props.artwork?.enhancementTier,
    props.artwork?.removeBackground,
    props.artwork?.isLoading,
    props.artwork?.id,
    props.artwork?.withBgMetadata?.widthInch,
    props.artwork?.enhancedMetadata?.widthInch
  ],
  async (
    [
      newEnhanceImage,
      newEnhancementTier,
      newRemoveBackground,
      newIsLoading,
      newId,
      newWithBgWidth,
      newEnhancedWidth
    ],
    [
      oldEnhanceImage,
      oldEnhancementTier,
      oldRemoveBackground,
      oldIsLoading,
      oldId,
      oldWithBgWidth,
      oldEnhancedWidth
    ]
  ) => {
    const isLoadingComplete = oldIsLoading && !newIsLoading;
    const artworkChanged = newId !== oldId;
    const enhancementSettingsChanged =
      newEnhanceImage !== oldEnhanceImage ||
      newEnhancementTier !== oldEnhancementTier ||
      newRemoveBackground !== oldRemoveBackground;
    const dimensionsChanged =
      newWithBgWidth !== oldWithBgWidth || newEnhancedWidth !== oldEnhancedWidth;

    if (
      !props.artwork?.isFile &&
      !props.artwork?.isSimulated &&
      !props.artwork?.isLoading &&
      (isLoadingComplete || artworkChanged || enhancementSettingsChanged || dimensionsChanged)
    ) {
      await updateDimensionsFromMetadata();
    }
  }
);

onMounted(async () => {
  // Update dimensions for artworks that are already loaded (e.g., from saved designs)
  if (
    !props.artwork?.isFile &&
    !props.artwork?.isSimulated &&
    !props.artwork?.isLoading &&
    props.artwork?.id
  ) {
    await updateDimensionsFromMetadata();
  } else {
    recalculateSizes();
  }
});
</script>

<style scoped lang="scss">
@import "frontend/init/jiffy/settings.scss";
@import "frontend/init/jiffy/mixin.scss";

.artwork-multi-dimensions {
  $this: &;

  min-height: 80px;

  &--multi-upload {
    #{$this}__field {
      &--number {
        width: 68px;
      }
    }

    #{$this}__control {
      &--quantity {
        margin-left: 0 !important;
      }
    }

    #{$this}__free-note {
      padding-top: 20px;
    }

    #{$this}__remove-button {
      height: 30px;
      margin-left: 0px;
    }
  }

  &__multiplier {
    padding: 5px;
    width: 18px;
    height: 18px;
    background-color: $section-grey;
    border-radius: 50%;
    flex-shrink: 0;
  }

  &__form-number-row {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 5px;
    margin-top: 5px;
    padding-bottom: 15px;

    &--first {
      margin-top: 0;

      #{$this}__multiplier {
        align-self: flex-end;
        margin-bottom: 13px;
      }

      #{$this}__remove-button {
        margin-top: 22px;
      }
    }
  }

  &__form-number-row-content {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    gap: 10px;
  }

  &__error-message {
    color: $red-light;
    font-size: 12px;
    font-weight: 400;
    line-height: 13px;
    margin-top: 5px;
    max-width: 345px;
  }

  &__control {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    position: relative;

    &--quantity {
      margin-left: 5px;

      @include with-desktop {
        margin-left: 15px;
      }
    }
  }

  &__control--gang-lock {
    #{$this}__field {
      @include with-large-desktop {
        background-image: url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTgiIHZpZXdCb3g9IjAgMCAxNiAxOCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTEyLjE2NjggNy4zMzMzM1Y1LjY2NjY3QzEyLjE2NjggMy4zNjU0OCAxMC4zMDEzIDEuNUM1LjY5ODk4IDEuNUMzLjgzMzUgMy4zNjU0OCAzLjgzMzUgNS42NjY2N1Y3LjMzMzMzTTguMDAwMTYgMTEuMDgzM1YxMi43NU01LjMzMzUgMTYuNUgxMC42NjY4QzEyLjA2NyAxNi41IDEyLjc2NyAxNi41IDEzLjMwMTggMTYuMjI3NUMxMy43NzIyIDE1Ljk4NzggMTQuMTU0NyAxNS42MDU0IDE0LjM5NDMgMTUuMTM1QzE0LjY2NjggMTQuNjAwMiAxNC42NjY4IDEzLjkwMDEgMTQuNjY2OCAxMi41VjExLjMzMzNDMTQuNjY2OCA5LjkzMzIgMTQuNjY2OCA5LjIzMzE0IDE0LjM5NDMgOC42OTgzNkMxNC4xNTQ3IDguMjI3OTUgMTMuNzcyMiA3Ljg0NTUgMTMuMzAxOCA3LjYwNTgyQzEyLjc2NyA3LjMzMzMzIDEyLjA2NyA3LjMzMzMzIDEwLjY2NjggNy4zMzMzM0g1LjMzMzVDMy45MzMzNiA3LjMzMzMzIDMuMjMzMyA3LjMzMzMzIDIuNjk4NTIgNy42MDU4MkMyLjIyODExIDcuODQ1NSAxLjg0NTY2IDguMjI3OTUgMS42MDU5OCA4LjY5ODM2QzEuMzMzNSA5LjIzMzE0IDEuMzMzNSA5LjkzMzIgMS4zMzM1IDExLjMzMzNWMTIuNUMxLjMzMzUgMTMuOTAwMSAxLjMzMzUgMTQuNjAwMiAxLjYwNTk4IDE1LjEzNUMxLjg0NTY2IDE1LjYwNTQgMi4yMjgxMSAxNS45ODc4IDIuNjk4NTIgMTYuMjI3NUMzLjIzMzMgMTYuNSAzLjkzMzM2IDE2LjUgNS4zMzM1IDE2LjVaIiBzdHJva2U9IiM3NTc1NzUiIHN0cm9rZS13aWR0aD0iMS41IiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz4KPC9zdmc+Cg==);
        background-repeat: no-repeat;
        background-position: 30%;
        padding-left: 18px;
      }
    }
  }

  &__label {
    display: block;
    font-size: 12px;
    font-weight: 400;
    line-height: 13px;
    text-align: center;
    white-space: nowrap;
    margin: 0 -5px 5px -5px;
  }

  &__field {
    height: 44px;
    border: 1px solid $gray-label;
    border-radius: 4px;
    text-align: center;

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      display: none;
    }

    &:read-only {
      background-color: $section-grey;
      outline: none;
      border: 1px solid $section-grey-border;
    }

    &:disabled {
      background: $section-grey;
    }

    &:focus {
      border: 2px solid $black;
    }

    &:focus:read-only {
      outline: none;
      border: 1px solid $section-grey-border;
    }

    &--number {
      width: 60px;
      -moz-appearance: textfield;

      @include with-desktop {
        width: 130px;
      }
    }

    &--invalid {
      border-color: $red-light;

      &:focus {
        border-color: $red-light;
        border-width: 2px;
        outline: none;
      }

      &:focus:read-only {
        outline: none;
        border: none;
      }
    }
  }

  &__add-button {
    width: 100%;
    height: 40px;
    padding: 8px 12px 8px 12px;
    gap: 8px;
    border: none;
    border-radius: 4px;
    background-color: #fff1f7;
    color: $dropzone-highlight;
    text-decoration: underline;
    font-size: 14px;
    font-weight: 700;
    margin-top: 8px;

    &--standard-artwork {
      background-color: $spindle-light;
      color: $primary-light;
    }

    &:disabled {
      color: $crafterpay-gray-modern-500;
      background-color: $section-grey;
    }
  }

  &__remove-button {
    border: none;
    padding: 0;
    background-color: $white;
    margin-top: 4px;
    margin-left: 5px;

    @include with-tablet {
      margin-left: 3px;
    }

    @include with-desktop {
      margin-left: 15px;
    }
  }

  &__remove-button-icon {
    height: 20px;
    width: 16px;
    color: $crafterpay-gray-modern-500;
    pointer-events: none;

    @include with-desktop {
      height: 24px;
      width: 24px;
    }
  }

  &__price {
    font-size: 14px;
    font-weight: 700;
    text-align: center;
    position: absolute;
    bottom: -23px;

    &--loading {
      opacity: 0.25;
    }
  }

  &__free-note {
    padding-top: 10px;
    font-weight: 400;
    text-align: center;
  }

  &__free-transfers-badge-wrapper {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 16px 0;
  }

  &__free-transfers-badge {
    margin: 0;
  }

  &__discount-text {
    font-size: 14px;
    color: $dark-blue2;
    white-space: nowrap;
    margin-left: auto;

    @media (max-width: 767px) {
      display: none;
    }
  }
}
</style>
